import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Key } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: Email, 2: Code & neues Passwort
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      toast.success('Ein Reset-Code wurde an Ihre E-Mail gesendet!');
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden des Reset-Codes';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Die Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword,
      });
      
      toast.success('Passwort erfolgreich zurückgesetzt!');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center space-x-2 mb-6">
          <Key className="text-blue-600" size={32} />
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Passwort vergessen
            </h1>
            <p className="text-sm text-slate-600">
              {step === 1 ? 'Reset-Code anfordern' : 'Neues Passwort setzen'}
            </p>
          </div>
        </div>

        {step === 1 ? (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <Label className="flex items-center space-x-2 mb-2">
                <Mail size={16} />
                <span>E-Mail-Adresse</span>
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="ihre.email@beispiel.de"
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1">
                Geben Sie die E-Mail-Adresse Ihres Kontos ein
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {loading ? 'Wird gesendet...' : 'Reset-Code anfordern'}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center space-x-1"
              >
                <ArrowLeft size={14} />
                <span>Zurück zum Login</span>
              </Link>
            </div>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label>Reset-Code</Label>
              <Input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                placeholder="6-stelliger Code aus der E-Mail"
                maxLength={6}
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1">
                Prüfen Sie Ihr E-Mail-Postfach
              </p>
            </div>

            <div>
              <Label>Neues Passwort</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Neues Passwort eingeben"
                minLength={6}
                className="w-full"
              />
              <p className="text-xs text-slate-500 mt-1">Mindestens 6 Zeichen</p>
            </div>

            <div>
              <Label>Passwort bestätigen</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Passwort erneut eingeben"
                minLength={6}
                className="w-full"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {loading ? 'Wird gesetzt...' : 'Passwort zurücksetzen'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Neuen Code anfordern
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
